import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useFinanceOverview, exportFinance } from '@/hooks/useFinance';
import Sparkline from '@/components/Sparkline';
import { Button } from '@/components/ui/button';
import DateInput from '@/components/DateInput';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Download, TrendingUp, TrendingDown } from 'lucide-react';
import { log } from '@/lib/log';

const formatINR = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

const PeriodButton = ({ value, label, current, onClick }) => (
  <button
    data-testid={`period-${value}`}
    onClick={() => onClick(value)}
    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
      current === value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    {label}
  </button>
);

const Card = ({ title, amount, deltaPct, sparkline, color, onClick, testid, accent, focused }) => {
  const positive = deltaPct >= 0;
  const Trend = positive ? TrendingUp : TrendingDown;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left bg-white rounded-2xl border-2 transition-all p-5 flex flex-col h-56 overflow-hidden ${
        focused ? 'border-primary ring-2 ring-primary/20 shadow-card-hover' : `${accent} hover:shadow-card-hover`
      }`}
    >
      <div className="text-xs font-semibold text-ink/60 tracking-wide">{title}</div>
      <div className="font-display text-3xl font-bold text-ink mt-2 tracking-tight">{formatINR(amount)}</div>
      <div className="flex-1 mt-3 min-h-0 -mx-1">
        <Sparkline data={sparkline} color={color} height={70} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${positive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          <Trend className="w-3.5 h-3.5" />
          {positive ? '+' : ''}{deltaPct}%
        </span>
        <span className="text-xs text-ink/50">vs previous period</span>
      </div>
    </button>
  );
};

const fmtDate = (s) => (s ? format(new Date(s), 'dd/MM/yy') : '—');

// --- per-focus tables ---
const IncomeTable = ({ rows, onRowClick }) => (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
    <div className="grid grid-cols-6 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold tracking-wide text-gray-600 ">
      <div className="col-span-2">Company Name</div>
      <div>Date</div>
      <div>Income</div>
      <div>Expense</div>
      <div>Pending</div>
    </div>
    {rows.length === 0 ? (
      <div className="p-12 text-center text-gray-500">No data for this period.</div>
    ) : (
      <div className="divide-y divide-gray-100">
        {rows.map((row, i) => (
          <div
            key={row.client_id || row.company_name || i}
            data-testid={`income-row-${i}`}
            onClick={() => row.client_id && onRowClick?.(row.client_id)}
            className={`grid grid-cols-6 gap-4 px-6 py-4 ${row.client_id ? 'cursor-pointer hover:bg-blue-50/40 transition-colors' : ''}`}
          >
            <div className={`col-span-2 font-medium ${row.client_id ? 'text-primary hover:underline' : 'text-gray-900'}`}>{row.company_name}</div>
            <div className="text-sm text-gray-600">{fmtDate(row.latest_date)}</div>
            <div className="text-sm text-gray-900">{formatINR(row.income)}</div>
            <div className="text-sm text-gray-900">{formatINR(row.expense)}</div>
            <div className="text-sm text-gray-900">{formatINR(row.pending)}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ExpenseView = ({ topCategories, statements }) => (
  <>
    {topCategories.length > 0 && (
      <>
        <h3 className="text-sm font-semibold tracking-wide text-gray-500  mb-3">Top Expense Categories</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {topCategories.slice(0, 8).map((c) => (
            <div key={c.sub_category} data-testid={`top-expense-${c.sub_category.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`} className="bg-white rounded-2xl border border-red-200 p-4">
              <div className="text-sm text-gray-600">{c.sub_category}</div>
              <div className="text-xl font-bold text-gray-900 mt-1">{formatINR(c.amount)}</div>
            </div>
          ))}
        </div>
      </>
    )}
    <h3 className="text-sm font-semibold tracking-wide text-gray-500  mb-3">Recent Expense Activity</h3>
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold tracking-wide text-gray-600 ">
        <div className="col-span-3">Company Name</div>
        <div className="col-span-2">Category</div>
        <div className="col-span-2">Date</div>
        <div className="col-span-2">Amount</div>
        <div className="col-span-3">Remark</div>
      </div>
      {statements.length === 0 ? (
        <div className="p-12 text-center text-gray-500">No expense statements in this period.</div>
      ) : (
        <div className="divide-y divide-gray-100">
          {statements.map((s, i) => (
            <div key={s.id || i} data-testid={`expense-row-${i}`} className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
              <div className="col-span-3 font-medium text-gray-900 truncate">{s.company_name}</div>
              <div className="col-span-2 text-sm text-gray-700">{s.sub_category}</div>
              <div className="col-span-2 text-sm text-gray-600">{fmtDate(s.date)}</div>
              <div className="col-span-2 text-sm text-gray-900">{formatINR(s.amount)}</div>
              <div className="col-span-3 text-sm text-gray-600 italic truncate">{s.remark || '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  </>
);

const SimpleList = ({ rows, amountKey, amountLabel, testidPrefix, onRowClick }) => (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
    <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold tracking-wide text-gray-600 ">
      <div className="col-span-6">Company Name</div>
      <div className="col-span-3">Date</div>
      <div className="col-span-3">{amountLabel}</div>
    </div>
    {rows.length === 0 ? (
      <div className="p-12 text-center text-gray-500">No data.</div>
    ) : (
      <div className="divide-y divide-gray-100">
        {rows.map((r, i) => (
          <div
            key={r.client_id || r.company_name || i}
            data-testid={`${testidPrefix}-row-${i}`}
            onClick={() => r.client_id && onRowClick?.(r.client_id)}
            className={`grid grid-cols-12 gap-4 px-6 py-4 ${r.client_id ? 'cursor-pointer hover:bg-blue-50/40 transition-colors' : ''}`}
          >
            <div className={`col-span-6 font-medium truncate ${r.client_id ? 'text-primary hover:underline' : 'text-gray-900'}`}>{r.company_name}</div>
            <div className="col-span-3 text-sm text-gray-600">{fmtDate(r.latest_date)}</div>
            <div className="col-span-3 text-sm text-gray-900">{formatINR(r[amountKey])}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const FinanceOverview = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('monthly');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [focus, setFocus] = useState('income'); // income | expense | pending | money_made
  const { data, loading } = useFinanceOverview(period, start, end);

  const handleExport = async (fmt) => {
    const params = { period };
    if (period === 'custom') {
      if (start) params.start = start;
      if (end) params.end = end;
    }
    try { await exportFinance(fmt, 'overview', params); }
    catch (err) { log.error('[FinanceOverview] export failed:', err); }
  };

  return (
    <div className="p-8" data-testid="finance-overview">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <h1 className="text-4xl font-semibold text-gray-900 tracking-tight">Finance Overview</h1>
        <div className="flex items-center gap-2">
          <Button data-testid="add-statement-btn" onClick={() => navigate('/finance/add')} className="rounded-xl bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> Add Statement
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="export-btn" variant="outline" className="rounded-xl">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem data-testid="export-pdf" onClick={() => handleExport('pdf')}>PDF</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-docx" onClick={() => handleExport('docx')}>DOCX</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-excel" onClick={() => handleExport('excel')}>Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <PeriodButton value="monthly" label="Monthly" current={period} onClick={setPeriod} />
        <PeriodButton value="quarterly" label="Quarterly" current={period} onClick={setPeriod} />
        <PeriodButton value="yearly" label="Yearly" current={period} onClick={setPeriod} />
        <PeriodButton value="custom" label="Custom" current={period} onClick={setPeriod} />
        <PeriodButton value="all" label="All Time" current={period} onClick={setPeriod} />
        {period === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <DateInput testid="custom-start" value={start} onChange={setStart} className="h-9 w-40" />
            <span className="text-gray-400">→</span>
            <DateInput testid="custom-end" value={end} onChange={setEnd} className="h-9 w-40" />
          </div>
        )}
        {data && (
          <span className="ml-auto text-xs text-gray-500">
            {data.current_range.start} → {data.current_range.end}
          </span>
        )}
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-2xl border-2 border-gray-100 h-56 bg-white animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <Card title="Income"     amount={data.cards.income.amount}     deltaPct={data.cards.income.delta_pct}     sparkline={data.cards.income.sparkline}     color="#10b981" accent="border-emerald-200" focused={focus==='income'}     onClick={() => setFocus('income')}     testid="card-income" />
            <Card title="Expense"    amount={data.cards.expense.amount}    deltaPct={data.cards.expense.delta_pct}    sparkline={data.cards.expense.sparkline}    color="#ef4444" accent="border-red-200"     focused={focus==='expense'}    onClick={() => setFocus('expense')}    testid="card-expense" />
            <Card title="Pending"    amount={data.cards.pending.amount}    deltaPct={data.cards.pending.delta_pct}    sparkline={data.cards.pending.sparkline}    color="#f59e0b" accent="border-amber-200"   focused={focus==='pending'}    onClick={() => setFocus('pending')}    testid="card-pending" />
            <Card title="Money Made" amount={data.cards.money_made.amount} deltaPct={data.cards.money_made.delta_pct} sparkline={data.cards.money_made.sparkline} color="#2563eb" accent="border-blue-200"    focused={focus==='money_made'} onClick={() => setFocus('money_made')} testid="card-money-made" />
          </div>

          <div data-testid={`focus-section-${focus}`}>
            {focus === 'income'     && <IncomeTable rows={data.by_company} onRowClick={(id) => navigate(`/finance/company/${id}`)} />}
            {focus === 'expense'    && <ExpenseView topCategories={data.top_expense_categories} statements={data.recent_expense_statements} />}
            {focus === 'pending'    && <SimpleList rows={data.pending_by_company}     amountKey="pending"    amountLabel="Pending Amount" testidPrefix="pending" onRowClick={(id) => navigate(`/finance/company/${id}`)} />}
            {focus === 'money_made' && <SimpleList rows={data.money_made_by_company}  amountKey="money_made" amountLabel="Money Made"     testidPrefix="money-made" onRowClick={(id) => navigate(`/finance/company/${id}`)} />}
          </div>
        </>
      )}
    </div>
  );
};

export default FinanceOverview;
